import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  RECOGNITION_BADGE_MAP, useMyRecognitionsApi, useRecognitionWallApi, useCreateRecognitionApi,
} from './config';
import SendRecognitionModal from './components/SendRecognitionModal';
import { useAuth } from '../../context/AuthContext';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import { formatDate } from '../../utils/Utils';

// Faz D (spec 2026-08-08 §4, Task 5, D5) — Takdir (Recognition): Duvar
// (public, sayfalı) + Aldıklarım/Gönderdiklerim (private dahil, task-3-report.md
// §1) + "Takdir Gönder" modalı. Tenant duvarı GET wall'dan (sahiplik/aktör
// aramaz, YALNIZ isPublic:true), kendi kayıtları GET my'den (sent/received —
// alan sırası spec §3'ün literal metin sırası, task-3-report.md §1) — iki uç
// arasında dolaşım YOK (bilinçli, aynı raporun concern'i).
const Container = styled.div``;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 12px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const BadgeIconTile = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $from, $to }) => `linear-gradient(135deg, ${$from || '#0291EF'}, ${$to || '#38BDF8'})`};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

const PersonNames = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DateText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const BadgeLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary.main};
  margin-bottom: 4px;
`;

const RecognitionText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const PrivateTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 10px;
  background: #F1F5F9;
  color: #667;
`;

const EmptyBox = styled.div`
  padding: 40px 18px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
`;

const Pager = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
`;

function RecognitionRow({ item, t, lang, counterpartName, showPrivateTag }) {
  const badge = RECOGNITION_BADGE_MAP[item.badgeKey];
  const Icon = badge?.Icon;
  return (
    <Card data-role={`recognition-row-${item.id}`}>
      <BadgeIconTile $from={badge?.from} $to={badge?.to}>
        {Icon && <Icon size={20} />}
      </BadgeIconTile>
      <CardBody>
        <CardTopRow>
          <PersonNames>{counterpartName}</PersonNames>
          <DateText>{formatDate(item.createDate, lang)}</DateText>
        </CardTopRow>
        <BadgeLabel>
          {t(`recognitions.badge.${item.badgeKey}`, { defaultValue: item.badgeKey })}
          {showPrivateTag && !item.isPublic && <PrivateTag style={{ marginLeft: 8 }}>{t('recognitions.privateTag')}</PrivateTag>}
        </BadgeLabel>
        {item.text && <RecognitionText>{item.text}</RecognitionText>}
      </CardBody>
    </Card>
  );
}

export default function RecognitionsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('wall');
  const [showModal, setShowModal] = useState(false);

  const wall = useRecognitionWallApi();
  const mine = useMyRecognitionsApi();
  const { create } = useCreateRecognitionApi();

  const currentEmployeeId = user?.employeeId;

  const tabs = [
    { key: 'wall', label: t('recognitions.tab.wall') },
    { key: 'received', label: t('recognitions.tab.received') },
    { key: 'sent', label: t('recognitions.tab.sent') },
  ];

  const handleCreate = useCallback(async (payload) => {
    const created = await create(payload);
    if (created) {
      wall.refresh();
      mine.refresh();
    }
    return created;
  }, [create, wall, mine]);

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('recognitions.pageTitle')}</PageTitle>
        <Button data-role="send-recognition-btn" onClick={() => setShowModal(true)}>
          {t('recognitions.sendButton')}
        </Button>
      </PageHeader>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'wall' && (
        <>
          {!wall.fetching && wall.items.length === 0 && (
            <EmptyBox>{t('recognitions.wallEmpty')}</EmptyBox>
          )}
          {wall.items.map((r) => (
            <RecognitionRow
              key={r.id}
              item={r}
              t={t}
              lang={i18n.language}
              counterpartName={`${r.senderName} → ${r.receiverName}`}
              showPrivateTag={false}
            />
          ))}
          {wall.items.length > 0 && (wall.hasPrev || wall.hasNext) && (
            <Pager>
              <Button
                variant="outline"
                disabled={!wall.hasPrev}
                onClick={() => wall.setPageNumber((p) => p - 1)}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                disabled={!wall.hasNext}
                onClick={() => wall.setPageNumber((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </Pager>
          )}
        </>
      )}

      {activeTab === 'received' && (
        <>
          {!mine.fetching && mine.received.length === 0 && (
            <EmptyBox>{t('recognitions.receivedEmpty')}</EmptyBox>
          )}
          {mine.received.map((r) => (
            <RecognitionRow
              key={r.id}
              item={r}
              t={t}
              lang={i18n.language}
              counterpartName={r.senderName}
              showPrivateTag
            />
          ))}
        </>
      )}

      {activeTab === 'sent' && (
        <>
          {!mine.fetching && mine.sent.length === 0 && (
            <EmptyBox>{t('recognitions.sentEmpty')}</EmptyBox>
          )}
          {mine.sent.map((r) => (
            <RecognitionRow
              key={r.id}
              item={r}
              t={t}
              lang={i18n.language}
              counterpartName={r.receiverName}
              showPrivateTag
            />
          ))}
        </>
      )}

      {showModal && (
        <SendRecognitionModal
          excludeEmployeeId={currentEmployeeId}
          onClose={() => setShowModal(false)}
          onCreate={handleCreate}
        />
      )}
    </Container>
  );
}
